<template>
  <div>
    <div class="d-flex justify-content-end mb-1">
      <b-button variant="primary" @click="openCreateModal">
        <feather-icon icon="PlusIcon" class="mr-50" /> Novo objetivo
      </b-button>
    </div>

    <span v-if="!secondaryGoals.length" class="text-muted">
      Nenhum objetivo secundário cadastrado ainda.
    </span>

    <b-row>
      <b-col
        v-for="goal in secondaryGoals"
        :key="goal.id"
        cols="12"
        md="6"
        lg="4"
        class="mb-1"
      >
        <b-card class="goal-card" no-body>
          <b-card-body>
            <div class="d-flex justify-content-between align-items-start">
              <h5 class="mb-0 goal-title">{{ goal.title }}</h5>
              <div>
                <b-button
                  variant="flat-primary"
                  size="sm"
                  class="btn-icon"
                  @click="openEditModal(goal)"
                >
                  <feather-icon icon="EditIcon" />
                </b-button>
                <b-button
                  variant="flat-danger"
                  size="sm"
                  class="btn-icon"
                  @click="removeGoal(goal)"
                >
                  <feather-icon icon="Trash2Icon" />
                </b-button>
              </div>
            </div>

            <p v-if="goal.motivation" class="text-muted small mt-1 mb-1">
              {{ goal.motivation }}
            </p>

            <div class="mt-1">
              <div class="d-flex justify-content-between">
                <small>{{ floatToMoney(goal.saved_amount) }}</small>
                <small>{{ floatToMoney(goal.target_amount) }}</small>
              </div>
              <b-progress :value="goalProgress(goal)" max="100" />
            </div>

            <small v-if="goal.target_date" class="text-muted d-block mt-1">
              Meta para {{ formatDate(goal.target_date) }}
            </small>
            <small v-if="priorityLabel(goal)" class="text-muted d-block">
              Prioridade: {{ priorityLabel(goal) }}
            </small>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Create / Edit Goal Modal -->
    <b-modal
      id="secondary-goal-modal"
      :title="form.id ? 'Editar objetivo' : 'Novo objetivo'"
      ok-title="Salvar"
      cancel-title="Cancelar"
      :ok-disabled="!canSubmit || saving"
      @ok.prevent="submitGoal"
      centered
    >
      <b-form-group label="Título">
        <b-form-input v-model="form.title" placeholder="Ex.: Viagem para a Europa" />
      </b-form-group>

      <b-form-group label="Motivação">
        <b-form-textarea v-model="form.motivation" rows="2" />
      </b-form-group>

      <MoneyInput
        id="target_amount"
        label="Valor alvo"
        v-model="form.target_amount"
      />

      <MoneyInput
        id="saved_amount"
        label="Valor já guardado"
        v-model="form.saved_amount"
      />

      <b-form-group label="Data alvo">
        <b-form-input v-model="form.target_date" type="date" />
      </b-form-group>

      <b-form-group label="Prioridade">
        <b-form-select v-model="form.priority" :options="priorityOptions" />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardBody,
  BButton,
  BModal,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormSelect,
  BProgress,
} from "bootstrap-vue";
import { mapActions, mapGetters } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import moment from "moment";
import { floatToMoney, moneyToFloat } from "@/helpers/converters";
import MoneyInput from "@/modules/shared/components/MoneyInput.vue";
import * as types from "../store/types";

// A API armazena priority como inteiro e ordena de forma ascendente
// (orderBy('priority')), ou seja: quanto menor o número, maior a prioridade.
const PRIORITY_HIGH = 1;
const PRIORITY_MEDIUM = 2;
const PRIORITY_LOW = 3;

const PRIORITY_LABELS = {
  [PRIORITY_HIGH]: "Alta",
  [PRIORITY_MEDIUM]: "Média",
  [PRIORITY_LOW]: "Baixa",
};

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BButton,
    BModal,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormSelect,
    BProgress,
    MoneyInput,
  },
  props: {
    token: {
      type: String,
      required: true,
    },
  },
  setup() {
    return { toast: useToast() };
  },
  data() {
    return {
      saving: false,
      form: this.blankForm(),
      priorityOptions: [
        { value: null, text: "Nenhuma" },
        { value: PRIORITY_HIGH, text: "Alta" },
        { value: PRIORITY_MEDIUM, text: "Média" },
        { value: PRIORITY_LOW, text: "Baixa" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      secondaryGoals: types.SECONDARY_GOALS,
    }),
    canSubmit() {
      return !!this.form.title && !!this.form.target_amount;
    },
  },
  mounted() {
    this.fetchAll();
  },
  methods: {
    ...mapActions({
      getSecondaryGoals: types.GET_SECONDARY_GOALS,
      createSecondaryGoal: types.CREATE_SECONDARY_GOAL,
      updateSecondaryGoal: types.UPDATE_SECONDARY_GOAL,
      deleteSecondaryGoal: types.DELETE_SECONDARY_GOAL,
    }),
    normalizePriority(priority) {
      const value = Number(priority);
      return PRIORITY_LABELS[value] ? value : null;
    },
    priorityLabel(goal) {
      return PRIORITY_LABELS[Number(goal.priority)] || "";
    },
    blankForm() {
      return {
        id: null,
        title: "",
        motivation: "",
        target_amount: null,
        saved_amount: null,
        target_date: null,
        priority: null,
      };
    },
    fetchAll() {
      this.getSecondaryGoals(this.token).catch(() => this.notifyError());
    },
    formatDate(date) {
      return moment(date).format("DD/MM/YYYY");
    },
    floatToMoney(value) {
      return floatToMoney(Number(value) || 0);
    },
    goalProgress(goal) {
      const target = Number(goal.target_amount) || 0;
      const saved = Number(goal.saved_amount) || 0;
      if (!target) return 0;
      return Math.min(100, Math.round((saved / target) * 100));
    },
    openCreateModal() {
      this.form = this.blankForm();
      this.$bvModal.show("secondary-goal-modal");
    },
    openEditModal(goal) {
      this.form = {
        id: goal.id,
        title: goal.title,
        motivation: goal.motivation,
        target_amount: floatToMoney(Number(goal.target_amount) || 0),
        saved_amount: floatToMoney(Number(goal.saved_amount) || 0),
        target_date: goal.target_date,
        priority: this.normalizePriority(goal.priority),
      };
      this.$bvModal.show("secondary-goal-modal");
    },
    submitGoal(evt) {
      if (evt && evt.preventDefault) evt.preventDefault();
      if (!this.canSubmit) return;
      this.saving = true;

      const payload = {
        token: this.token,
        title: this.form.title,
        motivation: this.form.motivation || null,
        target_amount: moneyToFloat(this.form.target_amount),
        saved_amount: this.form.saved_amount
          ? moneyToFloat(this.form.saved_amount)
          : 0,
        target_date: this.form.target_date || null,
        priority: this.form.priority ? Number(this.form.priority) : null,
      };

      const action = this.form.id
        ? this.updateSecondaryGoal({ ...payload, id: this.form.id })
        : this.createSecondaryGoal(payload);

      action
        .then(() => {
          this.$bvModal.hide("secondary-goal-modal");
          this.fetchAll();
          this.notify(
            "Sucesso!",
            this.form.id
              ? "O objetivo foi atualizado com sucesso."
              : "O objetivo foi criado com sucesso.",
            "success"
          );
        })
        .catch(() => {
          this.notify(
            "Oops!",
            "Não foi possível salvar o objetivo. Tente novamente.",
            "danger"
          );
        })
        .finally(() => {
          this.saving = false;
        });
    },
    async removeGoal(goal) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Deseja realmente excluir o objetivo "${goal.title}"?`,
        {
          title: "Excluir objetivo",
          okTitle: "Excluir",
          cancelTitle: "Cancelar",
          okVariant: "danger",
          centered: true,
        }
      );
      if (!confirmed) return;

      this.deleteSecondaryGoal({ token: this.token, id: goal.id })
        .then(() => {
          this.fetchAll();
          this.notify(
            "Objetivo excluído!",
            "O objetivo foi excluído com sucesso.",
            "success"
          );
        })
        .catch(() => {
          this.notifyError();
        });
    },
    notify(title, text, variant) {
      this.toast({
        component: ToastificationContent,
        props: {
          title,
          text,
          icon: variant === "danger" ? "AlertTriangleIcon" : "CheckCircleIcon",
          variant,
        },
      });
    },
    notifyError() {
      this.notify(
        "Oops!",
        "Ocorreu um erro. Entre em contato com o setor de TI.",
        "danger"
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.goal-card {
  height: 100%;
}
</style>
